import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft, ShieldCheck, Building2, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [preauthToken, setPreauthToken] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const { login, verifyTwoFactor, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  // Enterprise SSO dialog state
  const [ssoDialogOpen, setSsoDialogOpen] = useState(false);
  const [ssoEmail, setSsoEmail] = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);

  const handleSSOSubmit = async (e) => {
    e.preventDefault();
    const trimmed = (ssoEmail || '').trim().toLowerCase();
    if (!trimmed.includes('@')) {
      toast.error('Enter a valid work email');
      return;
    }
    setSsoLoading(true);
    try {
      const r = await axios.post(`${API}/auth/sso/initialize`, { email: trimmed });
      if (r.data?.authorization_url) {
        // Hand off to the IdP — the backend callback will bounce back to /auth/sso/complete.
        window.location.href = r.data.authorization_url;
      } else {
        toast.error('SSO init failed');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'SSO lookup failed';
      toast.error(msg);
    } finally {
      setSsoLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) {
        setPreauthToken(result.preauth_token);
        toast.info('Enter your authenticator code to finish login');
        return;
      }
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Structured error from the backend when domain is SSO-enforced.
      if (detail && typeof detail === 'object' && detail.code === 'sso_required') {
        toast.error(detail.message || 'SSO required');
        // Pre-fill and open the SSO dialog for the user so they finish in one click.
        setSsoEmail(email);
        setSsoDialogOpen(true);
      } else {
        toast.error((typeof detail === 'string' ? detail : null) || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFACode.trim()) { toast.error('Enter your 6-digit or recovery code'); return; }
    setLoading(true);
    try {
      await verifyTwoFactor(preauthToken, twoFACode.trim());
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Incorrect code');
    } finally {
      setLoading(false);
    }
  };

  const cancel2FA = () => {
    setPreauthToken(null);
    setTwoFACode('');
    setPassword('');
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-[#4B5563] hover:text-[#0A0A0A] mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <Card className="border-[#E5E7EB]">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src="/logo.png" alt="KStreak" className="w-10 h-10 rounded-lg object-contain" />
              <span className="font-semibold text-xl">KStreak</span>
            </div>
            <CardTitle className="text-2xl">Welcome back</CardTitle>
            <CardDescription>Sign in to access your knowledge base</CardDescription>
          </CardHeader>
          <CardContent>
            {preauthToken ? (
              <form onSubmit={handleVerify2FA} className="space-y-4" data-testid="twofa-login-form">
                <div className="text-center py-3">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-[#002FA7]/10 rounded-full mb-2">
                    <ShieldCheck className="w-6 h-6 text-[#002FA7]" />
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    Enter the 6-digit code from your authenticator app,<br />
                    or use a recovery code (XXXX-XXXX).
                  </p>
                </div>
                <div>
                  <Label htmlFor="twofa-code">Code</Label>
                  <Input
                    id="twofa-code"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value)}
                    placeholder="000000"
                    className="font-mono tracking-wider text-lg text-center"
                    autoFocus
                    data-testid="twofa-login-code-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#002FA7] hover:bg-[#00227A]"
                  disabled={loading || !twoFACode.trim()}
                  data-testid="twofa-login-submit"
                >
                  {loading ? 'Verifying...' : 'Verify and sign in'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={cancel2FA}
                  className="w-full text-xs"
                  data-testid="twofa-login-cancel"
                >
                  Cancel and start over
                </Button>
              </form>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    data-testid="password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#002FA7] hover:bg-[#00227A]"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
            )}

            {!preauthToken && (
            <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#E5E7EB]" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-[#9CA3AF]">or continue with</span>
              </div>
            </div>

            <Button 
              variant="outline" 
              className="w-full"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
            {/* Hidden fallback container for GSI renderButton (used when one-tap prompt is suppressed) */}
            <div id="g_id_signin_fallback" style={{ display: 'none' }}></div>

            <Button
              variant="outline"
              className="w-full mt-2 border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7]/5"
              onClick={() => setSsoDialogOpen(true)}
              data-testid="sso-login-btn"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Sign in with SSO
            </Button>

            <p className="text-center text-sm text-[#4B5563] mt-6">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#002FA7] hover:underline" data-testid="register-link">
                Sign up
              </Link>
            </p>
            <p className="text-center text-xs text-[#4B5563] mt-2">
              <Link to="/forgot-password" className="text-[#002FA7] hover:underline" data-testid="forgot-password-link">
                Forgot your password?
              </Link>
            </p>
            </>
            )}
          </CardContent>
        </Card>

        {/* Enterprise SSO email prompt */}
        <Dialog open={ssoDialogOpen} onOpenChange={setSsoDialogOpen}>
          <DialogContent className="max-w-sm" data-testid="sso-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#002FA7]" />Sign in with Enterprise SSO
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSSOSubmit} className="space-y-3">
              <p className="text-xs text-[#4B5563]">
                Enter your work email and we'll redirect you to your organization's identity provider (SAML or OIDC).
              </p>
              <div>
                <Label className="text-xs">Work email</Label>
                <Input
                  type="email"
                  value={ssoEmail}
                  onChange={(e) => setSsoEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="mt-1"
                  autoFocus
                  disabled={ssoLoading}
                  data-testid="sso-email-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSsoDialogOpen(false)} disabled={ssoLoading}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#002FA7] hover:bg-[#00227A]"
                  disabled={ssoLoading || !ssoEmail}
                  data-testid="sso-continue-btn"
                >
                  {ssoLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Continue
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default LoginPage;
